import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Compass, Eye } from "lucide-react";
import { LEAD_COLUMN_OPTIONS } from "@/lib/lead-column-options";

/**
 * Admin → Lead Settings panel.
 *
 * Houses workspace-wide lead-data settings:
 *   • Workspace-default visible columns in the Leads spreadsheet.
 *     Applies to every member who hasn't customized their own.
 *   • Destructive cleanup of direction tags (NE/NW/SE/SW) on leads.
 *
 * Backing endpoints (`PATCH /api/workspace/lead-column-defaults`,
 * `POST /api/lead-gen/clear-quadrant-tags`) are gated by requireRole on
 * the server, so this UI just keeps them out of non-admin reach.
 */
export function AdminLeadSettingsCard() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: defaults, isLoading } = useQuery<{ defaultHiddenLeadColumns: string[] }>({
    queryKey: ["/api/workspace/lead-column-defaults"],
  });
  const serverHidden = useMemo(() => new Set(defaults?.defaultHiddenLeadColumns ?? []), [defaults]);

  // Local draft so the admin can toggle a few then Save — avoids spamming PATCH
  // on every checkbox click, and lets them cancel without persisting.
  const [draftHidden, setDraftHidden] = useState<Set<string>>(new Set());
  useEffect(() => {
    setDraftHidden(new Set(defaults?.defaultHiddenLeadColumns ?? []));
  }, [defaults?.defaultHiddenLeadColumns]);

  const dirty = useMemo(() => {
    if (draftHidden.size !== serverHidden.size) return true;
    for (const k of draftHidden) if (!serverHidden.has(k)) return true;
    return false;
  }, [draftHidden, serverHidden]);

  const saveDefaultsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PATCH", "/api/workspace/lead-column-defaults", {
        defaultHiddenLeadColumns: Array.from(draftHidden),
      });
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/workspace/lead-column-defaults"] });
      qc.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({
        title: "Defaults saved",
        description: "Members who haven't customized their columns will see this immediately.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not save defaults.", variant: "destructive" });
    },
  });

  const toggleDraft = (key: string, visible: boolean) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const { data: sandbox } = useQuery<{ settings?: { quadrantTagsEnabled?: boolean } }>({
    queryKey: ["/api/lead-gen/sandbox"],
  });
  const quadrantTagsEnabled = sandbox?.settings?.quadrantTagsEnabled !== false;

  const quadrantToggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest("PATCH", "/api/lead-gen/settings", { quadrantTagsEnabled: enabled });
      return response.json();
    },
    onSuccess: (_data, enabled) => {
      qc.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      qc.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({
        title: enabled ? "Direction tags enabled" : "Direction tags disabled",
        description: enabled
          ? "Direction tags (NE/NW/SE/SW) are now visible on all leads."
          : "Direction tags are hidden from all leads. Stored data is preserved.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not update setting.", variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <Card data-testid="card-admin-lead-column-defaults">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Eye className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Workspace Default Columns</CardTitle>
              <CardDescription>
                Choose which fields appear in the Leads spreadsheet for everyone in this workspace.
                Individual users can override their own view from Settings → Lead Settings — once they
                do, your changes here won't yank columns out from under them.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
            {LEAD_COLUMN_OPTIONS.map((col) => {
              const isVisible = !draftHidden.has(col.key);
              return (
                <label
                  key={col.key}
                  className="flex items-center gap-2 text-sm cursor-pointer hover-elevate active-elevate-2 rounded-md p-2"
                  data-testid={`label-default-lead-column-${col.key}`}
                >
                  <Checkbox
                    checked={isVisible}
                    disabled={isLoading || saveDefaultsMutation.isPending}
                    onCheckedChange={(v) => toggleDraft(col.key, v === true)}
                    data-testid={`checkbox-default-lead-column-${col.key}`}
                  />
                  <span>{col.label}</span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={!dirty || saveDefaultsMutation.isPending}
              onClick={() => saveDefaultsMutation.mutate()}
              data-testid="button-save-default-lead-columns"
            >
              {saveDefaultsMutation.isPending ? "Saving…" : "Save defaults"}
            </Button>
            {dirty && (
              <Button
                size="sm"
                variant="ghost"
                disabled={saveDefaultsMutation.isPending}
                onClick={() => setDraftHidden(new Set(defaults?.defaultHiddenLeadColumns ?? []))}
                data-testid="button-cancel-default-lead-columns"
              >
                Cancel
              </Button>
            )}
            <p className="text-xs text-muted-foreground ml-auto">
              Account Name is always shown.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-admin-lead-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Compass className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Direction Tags</CardTitle>
              <CardDescription>
                Control whether NE/NW/SE/SW quadrant badges are shown on leads in this workspace.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <Switch
              id="quadrant-tags-toggle"
              checked={quadrantTagsEnabled}
              disabled={quadrantToggleMutation.isPending}
              onCheckedChange={(enabled) => quadrantToggleMutation.mutate(enabled)}
              data-testid="switch-quadrant-tags-enabled"
            />
            <div>
              <Label htmlFor="quadrant-tags-toggle" className="text-sm font-medium cursor-pointer">
                Direction Tags
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When off, direction tags (NE/NW/SE/SW) are hidden from all leads in this workspace.
                Stored data is preserved and tags reappear if you turn this back on.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
